/**
 * SurveyorCodeMatcher Component
 * 
 * Collaborative interface for surveyors to upload and match their field codes
 * to the master standard codes generated by AI.
 * 
 * Features:
 * - Add surveyors with name, initials, and unique color
 * - Upload codes from multiple formats (CSV, TXT, Excel-pasted, etc.)
 * - AI matches uploaded codes to master codes
 * - Drag-and-drop to manually adjust matches
 * - Accept/reject matches
 * - Color-coded by surveyor
 */

import React, { useState, useRef, useCallback } from 'react';
import { CodeDefinition, Surveyor, UploadedSurveyorCode, SurveyorCodeMatch, SurveyorCodeSession } from '../../contexts/types/CadManager.types';

interface SurveyorCodeMatcherProps {
  masterCodes: CodeDefinition[];
  session: SurveyorCodeSession;
  onSessionChange: (session: SurveyorCodeSession) => void;
  onAcceptMatches: (matches: SurveyorCodeMatch[]) => void;
  onAddMasterCode?: (code: CodeDefinition) => void;
  onClose: () => void;
}

// Predefined colors for surveyors
const SURVEYOR_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Sage
  '#FFEAA7', // Yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#F7DC6F', // Gold
  '#BB8FCE', // Lavender
  '#85C1E9', // Sky
];

// Generate unique ID
const generateId = () => Math.random().toString(36).substring(2, 11);

/**
 * Parse uploaded code file content
 * Supports: CSV, tab-delimited, space-delimited, one-per-line
 */
function parseCodeFile(content: string, surveyorId: string): UploadedSurveyorCode[] {
  const lines = content.split(/\r?\n/).filter(line => line.trim());
  const codes: UploadedSurveyorCode[] = [];
  
  for (const line of lines) {
    // Skip header-like lines
    if (line.toLowerCase().includes('code') && line.toLowerCase().includes('description')) continue;
    if (line.startsWith('#') || line.startsWith('//')) continue;
    
    // Try different delimiters
    let code = '';
    let description = '';
    
    if (line.includes('\t')) {
      // Tab-delimited
      const parts = line.split('\t');
      code = parts[0]?.trim() || '';
      description = parts[1]?.trim() || '';
    } else if (line.includes(',')) {
      // CSV
      const parts = line.split(',');
      code = parts[0]?.trim().replace(/"/g, '') || '';
      description = parts[1]?.trim().replace(/"/g, '') || '';
    } else if (line.includes('|')) {
      // Pipe-delimited (markdown)
      const parts = line.split('|').filter(p => p.trim());
      code = parts[0]?.trim() || '';
      description = parts[1]?.trim() || '';
    } else {
      // Space or single code per line
      const parts = line.trim().split(/\s+/);
      code = parts[0] || '';
      description = parts.slice(1).join(' ') || '';
    }
    
    // Clean and validate
    code = code.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
    
    if (code && code.length >= 1 && code.length <= 12) {
      codes.push({
        code,
        description: description || undefined,
        surveyorId,
        originalLine: line,
      });
    }
  }
  
  // Deduplicate
  const seen = new Set<string>();
  return codes.filter(c => {
    if (seen.has(c.code)) return false;
    seen.add(c.code);
    return true;
  });
}

/**
 * AI-based code matching using fuzzy matching and description context
 */
function matchCodesToMaster(
  uploadedCodes: UploadedSurveyorCode[],
  masterCodes: CodeDefinition[],
  existingMatches: SurveyorCodeMatch[]
): SurveyorCodeMatch[] {
  const matches: SurveyorCodeMatch[] = [];
  
  // Build lookup for existing accepted matches
  const acceptedMappings = new Map<string, string>();
  existingMatches
    .filter(m => m.status === 'accepted' && m.matchedMasterCode)
    .forEach(m => acceptedMappings.set(m.uploadedCode, m.matchedMasterCode!));
  
  for (const uploaded of uploadedCodes) {
    // Check if already accepted
    if (acceptedMappings.has(uploaded.code)) {
      matches.push({
        id: generateId(),
        uploadedCode: uploaded.code,
        surveyorId: uploaded.surveyorId,
        matchedMasterCode: acceptedMappings.get(uploaded.code),
        confidence: 100,
        status: 'accepted',
      });
      continue;
    }
    
    // Find best match
    let bestMatch: CodeDefinition | null = null;
    let bestScore = 0;
    
    for (const master of masterCodes) {
      let score = 0;
      
      // Exact match
      if (uploaded.code === master.code) {
        score = 100;
      }
      // Case-insensitive exact
      else if (uploaded.code.toLowerCase() === master.code.toLowerCase()) {
        score = 95;
      }
      // Code contains or is contained
      else if (uploaded.code.includes(master.code) || master.code.includes(uploaded.code)) {
        score = 70 + (Math.min(uploaded.code.length, master.code.length) / Math.max(uploaded.code.length, master.code.length)) * 20;
      }
      // Similar start
      else if (uploaded.code.substring(0, 2) === master.code.substring(0, 2)) {
        score = 40;
        // Boost if descriptions are similar
        if (uploaded.description && master.description) {
          const uploadDesc = uploaded.description.toLowerCase();
          const masterDesc = master.description.toLowerCase();
          const words = uploadDesc.split(/\s+/);
          const matchedWords = words.filter(w => masterDesc.includes(w) && w.length > 2);
          score += matchedWords.length * 10;
        }
      }
      // Description-based matching
      else if (uploaded.description && master.description) {
        const uploadDesc = uploaded.description.toLowerCase();
        const masterDesc = master.description.toLowerCase();
        const words = uploadDesc.split(/\s+/).filter(w => w.length > 2);
        const matchedWords = words.filter(w => masterDesc.includes(w));
        if (matchedWords.length >= 2) {
          score = 30 + matchedWords.length * 15;
        }
      }
      
      if (score > bestScore) {
        bestScore = score;
        bestMatch = master;
      }
    }
    
    matches.push({
      id: generateId(),
      uploadedCode: uploaded.code,
      surveyorId: uploaded.surveyorId,
      matchedMasterCode: bestScore >= 30 ? bestMatch?.code : undefined,
      confidence: bestScore,
      status: 'pending',
    });
  }
  
  return matches;
}

export const SurveyorCodeMatcher: React.FC<SurveyorCodeMatcherProps> = ({
  masterCodes,
  session,
  onSessionChange,
  onAcceptMatches,
  onAddMasterCode,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'surveyors' | 'upload' | 'match'>('surveyors');
  const [newSurveyorName, setNewSurveyorName] = useState('');
  const [newSurveyorInitials, setNewSurveyorInitials] = useState('');
  const [selectedSurveyorId, setSelectedSurveyorId] = useState<string | null>(null);
  const [uploadText, setUploadText] = useState('');
  const [draggedMatch, setDraggedMatch] = useState<SurveyorCodeMatch | null>(null);
  const [filterSurveyor, setFilterSurveyor] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'accepted' | 'unmatched'>('all');
  const [editingMatchId, setEditingMatchId] = useState<string | null>(null);
  const [masterCodeSearch, setMasterCodeSearch] = useState('');
  const [aiSearching, setAiSearching] = useState(false);
  const [aiSearchResults, setAiSearchResults] = useState<CodeDefinition[]>([]);
  
  // New code creation state
  const [creatingNewCode, setCreatingNewCode] = useState<SurveyorCodeMatch | null>(null);
  const [newCodeForm, setNewCodeForm] = useState<Partial<CodeDefinition>>({
    code: '',
    description: '',
    pointLayer: '',
    lineLayer: '',
  });
  const [editingLayerField, setEditingLayerField] = useState<'pointLayer' | 'lineLayer' | null>(null);
  const [layerSearch, setLayerSearch] = useState('');
  const [layerAiSearching, setLayerAiSearching] = useState(false);
  const [layerAiResults, setLayerAiResults] = useState<string[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Extract unique layers from master codes
  const uniqueLayers = React.useMemo(() => {
    const layers = new Set<string>();
    masterCodes.forEach(code => {
      if (code.pointLayer) layers.add(code.pointLayer);
      if (code.lineLayer) layers.add(code.lineLayer);
    });
    return Array.from(layers).sort();
  }, [masterCodes]);
  
  // Filter layers based on search
  const filteredLayers = layerSearch.trim()
    ? uniqueLayers.filter(layer => 
        layer.toLowerCase().includes(layerSearch.toLowerCase())
      ).slice(0, 15)
    : uniqueLayers.slice(0, 15);
  
  // Get next available color
  const getNextColor = () => {
    const usedColors = new Set(session.surveyors.map(s => s.color));
    return SURVEYOR_COLORS.find(c => !usedColors.has(c)) || SURVEYOR_COLORS[0];
  };
  
  // Add surveyor
  const handleAddSurveyor = () => {
    if (!newSurveyorName.trim()) return;
    
    const initials = newSurveyorInitials.trim() || 
      newSurveyorName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 3);
    
    const newSurveyor: Surveyor = {
      id: generateId(),
      name: newSurveyorName.trim(),
      initials,
      color: getNextColor(),
      createdAt: new Date().toISOString(),
    };
    
    onSessionChange({
      ...session,
      surveyors: [...session.surveyors, newSurveyor],
      lastUpdated: new Date().toISOString(),
    });
    
    setNewSurveyorName('');
    setNewSurveyorInitials('');
    setSelectedSurveyorId(newSurveyor.id);
  };
  
  // Remove surveyor
  const handleRemoveSurveyor = (id: string) => {
    onSessionChange({
      ...session,
      surveyors: session.surveyors.filter(s => s.id !== id),
      uploads: session.uploads.filter(u => u.surveyorId !== id),
      matches: session.matches.filter(m => m.surveyorId !== id),
      lastUpdated: new Date().toISOString(),
    });
    if (selectedSurveyorId === id) setSelectedSurveyorId(null);
  };
  
  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSurveyorId) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setUploadText(content);
    };
    reader.readAsText(file);
    
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [selectedSurveyorId]);
  
  // Process uploaded codes
  const handleProcessCodes = () => {
    if (!selectedSurveyorId || !uploadText.trim()) return;
    
    const newCodes = parseCodeFile(uploadText, selectedSurveyorId);
    
    // Remove existing codes from this surveyor
    const existingCodes = session.uploads.filter(u => u.surveyorId !== selectedSurveyorId);
    const updatedUploads = [...existingCodes, ...newCodes];
    
    // Re-match all codes for this surveyor
    const otherMatches = session.matches.filter(m => m.surveyorId !== selectedSurveyorId);
    const newMatches = matchCodesToMaster(newCodes, masterCodes, session.matches);
    
    onSessionChange({
      ...session,
      uploads: updatedUploads,
      matches: [...otherMatches, ...newMatches],
      lastUpdated: new Date().toISOString(),
    });
    
    setUploadText('');
    setActiveTab('match');
  };
  
  // Accept a match
  const handleAcceptMatch = (matchId: string) => {
    onSessionChange({
      ...session,
      matches: session.matches.map(m => 
        m.id === matchId 
          ? { ...m, status: 'accepted' as const, acceptedAt: new Date().toISOString() }
          : m
      ),
      lastUpdated: new Date().toISOString(),
    });
  };
  
  // Reject a match
  const handleRejectMatch = (matchId: string) => {
    onSessionChange({
      ...session,
      matches: session.matches.map(m => 
        m.id === matchId 
          ? { ...m, status: 'rejected' as const, matchedMasterCode: undefined }
          : m
      ),
      lastUpdated: new Date().toISOString(),
    });
  };
  
  // Manual match assignment via drag
  const handleDropOnMaster = (masterCode: string) => {
    if (!draggedMatch) return;
    
    onSessionChange({
      ...session,
      matches: session.matches.map(m => 
        m.id === draggedMatch.id 
          ? { ...m, matchedMasterCode: masterCode, status: 'manual' as const, confidence: 100 }
          : m
      ),
      lastUpdated: new Date().toISOString(),
    });
    
    setDraggedMatch(null);
  };
  
  // Reassign master code for a match
  const handleReassignMasterCode = (matchId: string, newMasterCode: string) => {
    onSessionChange({
      ...session,
      matches: session.matches.map(m => 
        m.id === matchId 
          ? { ...m, matchedMasterCode: newMasterCode, status: 'manual' as const, confidence: 100 }
          : m
      ),
      lastUpdated: new Date().toISOString(),
    });
    setEditingMatchId(null);
    setMasterCodeSearch('');
  };
  
  // Filter master codes based on search
  const filteredMasterCodes = masterCodeSearch.trim()
    ? masterCodes.filter(mc => 
        mc.code.toLowerCase().includes(masterCodeSearch.toLowerCase()) ||
        mc.description?.toLowerCase().includes(masterCodeSearch.toLowerCase())
      ).slice(0, 15)
    : masterCodes.slice(0, 15);
  
  // AI semantic search for finding matches
  const handleAiSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    setAiSearching(true);
    setAiSearchResults([]);
    
    try {
      // Semantic matching using word similarity and context
      const query = searchQuery.toLowerCase();
      const queryWords = query.split(/\s+/).filter(w => w.length > 1);
      
      // Score each master code based on semantic similarity
      const scored = masterCodes.map(mc => {
        let score = 0;
        const desc = (mc.description || '').toLowerCase();
        const code = mc.code.toLowerCase();
        
        // Common abbreviation mappings for survey codes
        const abbreviations: Record<string, string[]> = {
          'ac': ['air conditioner', 'air conditioning', 'a/c', 'hvac', 'cooling'],
          'wm': ['water meter', 'meter'],
          'gm': ['gas meter', 'meter'],
          'em': ['electric meter', 'meter', 'electrical'],
          'mh': ['manhole', 'man hole'],
          'cb': ['catch basin', 'storm drain', 'inlet'],
          'fh': ['fire hydrant', 'hydrant'],
          'lp': ['light pole', 'lamp post', 'street light'],
          'pp': ['power pole', 'utility pole', 'electric pole'],
          'tp': ['telephone pole', 'phone pole', 'telecom'],
          'ss': ['sanitary sewer', 'sewer'],
          'st': ['storm', 'storm drain'],
          'wv': ['water valve', 'valve'],
          'gv': ['gas valve', 'valve'],
          'co': ['cleanout', 'clean out', 'clean-out'],
          'ip': ['iron pipe', 'iron pin', 'monument'],
          'ep': ['edge', 'pavement', 'asphalt'],
          'tc': ['top', 'curb'],
          'bc': ['back', 'curb'],
          'fl': ['flow line', 'flowline', 'invert'],
          'bldg': ['building', 'structure'],
          'conc': ['concrete'],
          'elec': ['electric', 'electrical'],
          'util': ['utility', 'utilities'],
          'trans': ['transformer', 'electrical'],
          'ped': ['pedestal', 'cabinet', 'box'],
        };
        
        // Check if query matches known abbreviations
        for (const [abbr, meanings] of Object.entries(abbreviations)) {
          if (code.includes(abbr) || desc.includes(abbr)) {
            for (const meaning of meanings) {
              if (query.includes(meaning) || meaning.includes(query)) {
                score += 50;
                break;
              }
            }
          }
        }
        
        // Word overlap scoring
        for (const word of queryWords) {
          if (desc.includes(word)) score += 20;
          if (code.includes(word)) score += 15;
          
          // Partial word matching
          const descWords = desc.split(/\s+/);
          for (const dw of descWords) {
            if (dw.startsWith(word) || word.startsWith(dw)) score += 10;
          }
        }
        
        // Category matching
        const categories: Record<string, string[]> = {
          'utility': ['meter', 'valve', 'pipe', 'line', 'main', 'service'],
          'electrical': ['transformer', 'pole', 'light', 'meter', 'panel', 'box'],
          'water': ['hydrant', 'valve', 'meter', 'main', 'service', 'well'],
          'sewer': ['manhole', 'cleanout', 'line', 'main', 'lateral'],
          'storm': ['inlet', 'catch', 'drain', 'culvert', 'headwall'],
          'survey': ['monument', 'pin', 'pipe', 'cap', 'nail', 'mag'],
          'building': ['corner', 'foundation', 'wall', 'structure'],
        };
        
        for (const [cat, terms] of Object.entries(categories)) {
          const queryMatchesCat = query.includes(cat) || terms.some(t => query.includes(t));
          const codeMatchesCat = desc.includes(cat) || terms.some(t => desc.includes(t));
          if (queryMatchesCat && codeMatchesCat) score += 15;
        }
        
        return { code: mc, score };
      });
      
      // Sort by score and take top results
      const results = scored
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10)
        .map(s => s.code);
      
      setAiSearchResults(results);
    } catch (error) {
      console.error('AI search error:', error);
    } finally {
      setAiSearching(false);
    }
  }, [masterCodes]);
  
  // AI semantic search for layers
  const handleLayerAiSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    setLayerAiSearching(true);
    setLayerAiResults([]);
    
    try {
      const query = searchQuery.toLowerCase();
      
      // A/E/C CAD Standard discipline codes and their meanings
      const disciplines: Record<string, string[]> = {
        'A': ['architectural', 'architecture', 'building', 'interior'],
        'C': ['civil', 'site', 'grading', 'paving', 'road'],
        'E': ['electrical', 'power', 'lighting', 'electric'],
        'F': ['fire', 'fire protection', 'sprinkler'],
        'G': ['general', 'survey', 'boundary', 'control'],
        'H': ['hazardous', 'hazmat', 'environmental'],
        'I': ['interiors', 'furniture', 'fixtures'],
        'L': ['landscape', 'planting', 'irrigation'],
        'M': ['mechanical', 'hvac', 'plumbing', 'piping'],
        'P': ['plumbing', 'pipe', 'sanitary', 'water'],
        'Q': ['equipment', 'machinery'],
        'S': ['structural', 'foundation', 'framing'],
        'T': ['telecommunications', 'telecom', 'data', 'network'],
        'V': ['survey', 'topographic', 'topo', 'utility'],
        'X': ['other', 'reference', 'existing'],
        'Z': ['contractor', 'shop'],
      };
      
      // Common layer name components
      const components: Record<string, string[]> = {
        'TOPO': ['topographic', 'topo', 'terrain', 'contour', 'elevation'],
        'STRM': ['storm', 'storm drain', 'drainage', 'inlet', 'catch basin'],
        'WATR': ['water', 'water line', 'water main', 'hydrant'],
        'SSWR': ['sanitary', 'sewer', 'sanitary sewer'],
        'UTIL': ['utility', 'utilities', 'underground'],
        'CTRL': ['control', 'monument', 'benchmark', 'survey control'],
        'BLDG': ['building', 'structure', 'foundation'],
        'ROAD': ['road', 'street', 'pavement', 'asphalt'],
        'CURB': ['curb', 'gutter', 'sidewalk'],
        'PROP': ['property', 'boundary', 'lot line'],
        'ESMT': ['easement', 'right of way', 'row'],
        'ELEC': ['electric', 'electrical', 'power'],
        'GAS': ['gas', 'gas line', 'natural gas'],
        'COMM': ['communication', 'telecom', 'fiber', 'cable'],
        'SIGN': ['sign', 'signage', 'traffic sign'],
        'LITE': ['light', 'lighting', 'lamp', 'pole'],
        'TREE': ['tree', 'vegetation', 'landscape'],
        'FENC': ['fence', 'fencing', 'barrier'],
        'WALL': ['wall', 'retaining wall'],
        'CONC': ['concrete', 'sidewalk', 'flatwork'],
        'PVMT': ['pavement', 'asphalt', 'paving'],
        'SPOT': ['spot', 'spot elevation', 'grade'],
        'CONT': ['contour', 'elevation', 'grade'],
      };
      
      // Score each unique layer
      const scored = uniqueLayers.map(layer => {
        let score = 0;
        const layerUpper = layer.toUpperCase();
        const layerParts = layerUpper.split('-');
        
        // Check discipline match
        const discipline = layerParts[0];
        if (disciplines[discipline]) {
          for (const meaning of disciplines[discipline]) {
            if (query.includes(meaning)) {
              score += 30;
              break;
            }
          }
        }
        
        // Check component matches
        for (const [comp, meanings] of Object.entries(components)) {
          if (layerUpper.includes(comp)) {
            for (const meaning of meanings) {
              if (query.includes(meaning)) {
                score += 40;
                break;
              }
            }
          }
        }
        
        // Direct text match
        if (layer.toLowerCase().includes(query)) {
          score += 50;
        }
        
        return { layer, score };
      });
      
      const results = scored
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10)
        .map(s => s.layer);
      
      setLayerAiResults(results);
    } catch (error) {
      console.error('Layer AI search error:', error);
    } finally {
      setLayerAiSearching(false);
    }
  }, [uniqueLayers]);
  
  // Accept all pending matches with high confidence
  const handleAcceptAllHighConfidence = () => {
    onSessionChange({
      ...session,
      matches: session.matches.map(m => 
        m.status === 'pending' && m.confidence >= 80
          ? { ...m, status: 'accepted' as const, acceptedAt: new Date().toISOString() }
          : m
      ),
      lastUpdated: new Date().toISOString(),
    });
  };
  
  // Finalize - add accepted codes as aliases to master codes
  const handleFinalize = () => {
    const acceptedMatches = session.matches.filter(m => m.status === 'accepted' && m.matchedMasterCode);
    onAcceptMatches(acceptedMatches);
  };
  
  // Open create new code modal
  const handleOpenCreateCode = (match: SurveyorCodeMatch) => {
    const uploadedCode = session.uploads.find(u => u.code === match.uploadedCode);
    setCreatingNewCode(match);
    setNewCodeForm({
      code: match.uploadedCode,
      description: uploadedCode?.description || '',
      pointLayer: '',
      lineLayer: '',
    });
  };
  
  // Create new master code and auto-accept the match
  const handleCreateNewCode = () => {
    if (!creatingNewCode || !newCodeForm.code || !onAddMasterCode) return;
    
    const newCode: CodeDefinition = {
      code: newCodeForm.code.toUpperCase(),
      description: newCodeForm.description || '',
      pointLayer: newCodeForm.pointLayer || '',
      lineLayer: newCodeForm.lineLayer || '',
      surveyorId: creatingNewCode.surveyorId, // Track which surveyor added this code
    };
    
    // Add to master codes via callback
    onAddMasterCode(newCode);
    
    // Auto-accept the match
    onSessionChange({
      ...session,
      matches: session.matches.map(m => 
        m.id === creatingNewCode.id
          ? { 
              ...m, 
              matchedMasterCode: newCode.code, 
              status: 'accepted' as const, 
              confidence: 100,
              acceptedAt: new Date().toISOString() 
            }
          : m
      ),
      lastUpdated: new Date().toISOString(),
    });
    
    // Close modal
    setCreatingNewCode(null);
    setNewCodeForm({
      code: '',
      description: '',
      pointLayer: '',
      lineLayer: '',
    });
  };
  
  // Get surveyor by ID
  const getSurveyor = (id: string) => session.surveyors.find(s => s.id === id);
  
  // Filter matches for display
  const filteredMatches = session.matches.filter(m => {
    if (filterSurveyor !== 'all' && m.surveyorId !== filterSurveyor) return false;
    if (filterStatus === 'pending' && m.status !== 'pending') return false;
    if (filterStatus === 'accepted' && m.status !== 'accepted') return false;
    if (filterStatus === 'unmatched' && m.matchedMasterCode) return false;
    return true;
  });
  
  // Stats
  const totalMatches = session.matches.length;
  const acceptedCount = session.matches.filter(m => m.status === 'accepted').length;
  const pendingCount = session.matches.filter(m => m.status === 'pending').length;
  const unmatchedCount = session.matches.filter(m => !m.matchedMasterCode).length;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-[98vw] h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              👥 Surveyor Code Matcher
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Upload and match surveyor field codes to master standards
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('surveyors')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'surveyors'
                ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-400/10'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            👤 Surveyors ({session.surveyors.length})
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-400/10'
                : 'text-gray-400 hover:text-white'
            }`}
            disabled={session.surveyors.length === 0}
          >
            📤 Upload Codes
          </button>
          <button
            onClick={() => setActiveTab('match')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'match'
                ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-400/10'
                : 'text-gray-400 hover:text-white'
            }`}
            disabled={session.matches.length === 0}
          >
            🔗 Match Codes ({totalMatches})
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {/* Surveyors Tab */}
          {activeTab === 'surveyors' && (
            <div className="p-6 h-full overflow-auto">
              <div className="max-w-2xl mx-auto space-y-6">
                {/* Add Surveyor Form */}
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Add Surveyor</h3>
                  <div className="flex gap-4">
                    <input
                      type="text"
                      value={newSurveyorName}
                      onChange={(e) => setNewSurveyorName(e.target.value)}
                      placeholder="Full Name"
                      className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={newSurveyorInitials}
                      onChange={(e) => setNewSurveyorInitials(e.target.value.toUpperCase().slice(0, 3))}
                      placeholder="Initials"
                      maxLength={3}
                      className="w-24 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none text-center"
                    />
                    <button
                      onClick={handleAddSurveyor}
                      disabled={!newSurveyorName.trim()}
                      className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>
                
                {/* Surveyors List */}
                <div className="space-y-3">
                  {session.surveyors.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-4xl mb-2">👤</p>
                      <p>No surveyors added yet</p>
                      <p className="text-sm">Add surveyors to upload their field codes</p>
                    </div>
                  ) : (
                    session.surveyors.map(surveyor => {
                      const surveyorCodes = session.uploads.filter(u => u.surveyorId === surveyor.id);
                      const surveyorMatches = session.matches.filter(m => m.surveyorId === surveyor.id);
                      const acceptedMatches = surveyorMatches.filter(m => m.status === 'accepted');
                      
                      return (
                        <div
                          key={surveyor.id}
                          className={`flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border transition-colors ${
                            selectedSurveyorId === surveyor.id
                              ? 'border-cyan-500'
                              : 'border-gray-700 hover:border-gray-600'
                          }`}
                          onClick={() => setSelectedSurveyorId(surveyor.id)}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white"
                              style={{ backgroundColor: surveyor.color }}
                            >
                              {surveyor.initials}
                            </div>
                            <div>
                              <p className="font-medium text-white">{surveyor.name}</p>
                              <p className="text-sm text-gray-400">
                                {surveyorCodes.length} codes uploaded • {acceptedMatches.length}/{surveyorMatches.length} matched
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSurveyorId(surveyor.id);
                                setActiveTab('upload');
                              }}
                              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded transition-colors"
                            >
                              Upload Codes
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveSurveyor(surveyor.id);
                              }}
                              className="p-1.5 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="p-6 h-full overflow-auto">
              <div className="max-w-3xl mx-auto space-y-6">
                {/* Surveyor Selector */}
                <div className="flex items-center gap-4">
                  <span className="text-gray-400">Upload codes for:</span>
                  <select
                    value={selectedSurveyorId || ''}
                    onChange={(e) => setSelectedSurveyorId(e.target.value || null)}
                    className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  >
                    <option value="">Select surveyor...</option>
                    {session.surveyors.map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.initials})</option>
                    ))}
                  </select>
                </div>
                
                {selectedSurveyorId ? (
                  <>
                    {/* File Upload */}
                    <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700 border-dashed text-center">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt,.tsv"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                      >
                        📁 Choose File
                      </button>
                      <p className="text-sm text-gray-500 mt-2">
                        Supports CSV, TXT, TSV (tab-delimited)
                      </p>
                    </div>
                    
                    {/* Or paste */}
                    <div className="text-center text-gray-500 text-sm">— or paste codes below —</div>
                    
                    {/* Text Input */}
                    <div>
                      <textarea
                        value={uploadText}
                        onChange={(e) => setUploadText(e.target.value)}
                        placeholder={`Paste codes here, one per line:\n\nIPF\nIPS\nMH,Manhole\nEP,Edge of Pavement\n...`}
                        className="w-full h-64 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm placeholder-gray-600 focus:border-cyan-500 focus:outline-none resize-none"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-sm text-gray-500">
                          Format: CODE or CODE,Description (comma, tab, or space separated)
                        </p>
                        <p className="text-sm text-gray-500">
                          {uploadText.split('\n').filter(l => l.trim()).length} lines
                        </p>
                      </div>
                    </div>
                    
                    {/* Process Button */}
                    <button
                      onClick={handleProcessCodes}
                      disabled={!uploadText.trim()}
                      className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg font-medium transition-all disabled:cursor-not-allowed"
                    >
                      🔍 Process & Match Codes
                    </button>
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p>Select a surveyor to upload their codes</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Match Tab */}
          {activeTab === 'match' && (
            <div className="h-full flex flex-col">
              {/* Stats Bar */}
              <div className="flex items-center justify-between px-6 py-3 bg-gray-800/50 border-b border-gray-700">
                <div className="flex items-center gap-6 text-sm">
                  <span className="text-gray-400">
                    Total: <span className="text-white font-medium">{totalMatches}</span>
                  </span>
                  <span className="text-green-400">
                    ✓ Accepted: <span className="font-medium">{acceptedCount}</span>
                  </span>
                  <span className="text-yellow-400">
                    ⏳ Pending: <span className="font-medium">{pendingCount}</span>
                  </span>
                  <span className="text-red-400">
                    ✗ Unmatched: <span className="font-medium">{unmatchedCount}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={filterSurveyor}
                    onChange={(e) => setFilterSurveyor(e.target.value)}
                    className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  >
                    <option value="all">All Surveyors</option>
                    {session.surveyors.map(s => (
                      <option key={s.id} value={s.id}>{s.initials}</option>
                    ))}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as any)}
                    className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="unmatched">Unmatched</option>
                  </select>
                  <button
                    onClick={handleAcceptAllHighConfidence}
                    className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded font-medium transition-colors"
                  >
                    ✓ Accept All 80%+
                  </button>
                </div>
              </div>
              
              {/* Match Table */}
              <div className="flex-1 overflow-auto p-4">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-900 z-10">
                    <tr className="text-left text-sm text-gray-400 border-b border-gray-700">
                      <th className="pb-2 w-16">User</th>
                      <th className="pb-2 w-32">Uploaded Code</th>
                      <th className="pb-2 w-20 text-center">→</th>
                      <th className="pb-2 w-32">Master Code</th>
                      <th className="pb-2">Master Description</th>
                      <th className="pb-2 w-24 text-center">Confidence</th>
                      <th className="pb-2 w-32 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMatches.map(match => {
                      const surveyor = getSurveyor(match.surveyorId);
                      const masterCode = masterCodes.find(c => c.code === match.matchedMasterCode);
                      
                      return (
                        <tr
                          key={match.id}
                          className={`border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${
                            match.status === 'accepted' ? 'bg-green-500/5' : ''
                          }`}
                          draggable={match.status !== 'accepted'}
                          onDragStart={() => setDraggedMatch(match)}
                          onDragEnd={() => setDraggedMatch(null)}
                        >
                          <td className="py-2">
                            {surveyor && (
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                style={{ backgroundColor: surveyor.color }}
                                title={surveyor.name}
                              >
                                {surveyor.initials}
                              </div>
                            )}
                          </td>
                          <td className="py-2">
                            <span
                              className="font-mono font-bold"
                              style={{ color: surveyor?.color || '#fff' }}
                            >
                              {match.uploadedCode}
                            </span>
                          </td>
                          <td className="py-2 text-center text-gray-500">
                            {match.matchedMasterCode ? '→' : '?'}
                          </td>
                          <td
                            className="py-2 relative"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => handleDropOnMaster(match.matchedMasterCode || '')}
                          >
                            {editingMatchId === match.id ? (
                              <div className="relative">
                                <input
                                  ref={searchInputRef}
                                  type="text"
                                  value={masterCodeSearch}
                                  onChange={(e) => setMasterCodeSearch(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      setEditingMatchId(null);
                                      setMasterCodeSearch('');
                                      setAiSearchResults([]);
                                    }
                                  }}
                                  placeholder="Search codes..."
                                  className="w-full px-2 py-1 bg-gray-700 border border-cyan-500 rounded text-white text-sm focus:outline-none"
                                  autoFocus
                                />
                                {/* Dropdown */}
                                <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-20 max-h-80 overflow-auto min-w-[400px]">
                                  {/* AI Search Results */}
                                  {aiSearchResults.length > 0 && (
                                    <>
                                      <div className="px-3 py-1.5 bg-purple-500/20 text-purple-300 text-xs font-medium border-b border-gray-700 flex items-center gap-2">
                                        ✨ AI Suggested Matches
                                      </div>
                                      {aiSearchResults.map(mc => (
                                        <button
                                          key={`ai-${mc.code}`}
                                          onClick={() => handleReassignMasterCode(match.id, mc.code)}
                                          className="w-full px-3 py-2 text-left hover:bg-purple-500/20 flex items-start gap-3 border-b border-gray-700"
                                        >
                                          <span className="font-mono font-bold text-purple-400 w-20 flex-shrink-0">{mc.code}</span>
                                          <span className="text-gray-300 text-sm">{mc.description || '—'}</span>
                                        </button>
                                      ))}
                                      <div className="border-b-2 border-gray-600" />
                                    </>
                                  )}
                                  
                                  {/* Regular search results */}
                                  {filteredMasterCodes.length > 0 && (
                                    <>
                                      {aiSearchResults.length > 0 && (
                                        <div className="px-3 py-1.5 bg-gray-700/50 text-gray-400 text-xs font-medium border-b border-gray-700">
                                          Text Matches
                                        </div>
                                      )}
                                      {filteredMasterCodes.map(mc => (
                                        <button
                                          key={mc.code}
                                          onClick={() => handleReassignMasterCode(match.id, mc.code)}
                                          className="w-full px-3 py-2 text-left hover:bg-gray-700 flex items-start gap-3 border-b border-gray-700 last:border-0"
                                        >
                                          <span className="font-mono font-bold text-cyan-400 w-20 flex-shrink-0">{mc.code}</span>
                                          <span className="text-gray-300 text-sm">{mc.description || '—'}</span>
                                        </button>
                                      ))}
                                    </>
                                  )}
                                  
                                  {/* No results + AI Search button */}
                                  {filteredMasterCodes.length === 0 && aiSearchResults.length === 0 && !aiSearching && (
                                    <div className="px-3 py-3 text-center">
                                      <p className="text-gray-500 text-sm mb-2">No direct matches found</p>
                                      <button
                                        onClick={() => handleAiSearch(masterCodeSearch)}
                                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
                                      >
                                        ✨ AI Search
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* AI Search button when results exist but user wants more */}
                                  {filteredMasterCodes.length > 0 && aiSearchResults.length === 0 && masterCodeSearch.trim() && (
                                    <button
                                      onClick={() => handleAiSearch(masterCodeSearch)}
                                      disabled={aiSearching}
                                      className="w-full px-3 py-2.5 text-left hover:bg-purple-500/20 flex items-center gap-2 text-purple-400 border-t border-gray-600"
                                    >
                                      {aiSearching ? (
                                        <><span className="animate-spin">⏳</span> Searching...</>
                                      ) : (
                                        <>✨ Try AI Search for "{masterCodeSearch}"</>
                                      )}
                                    </button>
                                  )}
                                  
                                  {/* Loading state */}
                                  {aiSearching && (
                                    <div className="px-3 py-4 text-center text-purple-400">
                                      <span className="animate-pulse">✨ AI searching...</span>
                                    </div>
                                  )}
                                </div>
                                {/* Click outside to close */}
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => {
                                    setEditingMatchId(null);
                                    setMasterCodeSearch('');
                                    setAiSearchResults([]);
                                  }}
                                />
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingMatchId(match.id);
                                  setMasterCodeSearch('');
                                  setAiSearchResults([]);
                                }}
                                className="font-mono font-bold text-cyan-400 hover:text-cyan-300 hover:underline cursor-pointer transition-colors"
                                title="Click to change match"
                              >
                                {match.matchedMasterCode || <span className="text-gray-600 italic">No match</span>}
                              </button>
                            )}
                          </td>
                          <td className="py-2 text-gray-400 text-sm">
                            {masterCode?.description || '—'}
                          </td>
                          <td className="py-2 text-center">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${
                                match.confidence >= 80 ? 'bg-green-500/20 text-green-400' :
                                match.confidence >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {match.confidence}%
                            </span>
                          </td>
                          <td className="py-2 text-center">
                            {match.status === 'accepted' ? (
                              <span className="text-green-400 text-sm">✓ Accepted</span>
                            ) : (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleAcceptMatch(match.id)}
                                  disabled={!match.matchedMasterCode}
                                  className="px-2 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white text-xs rounded transition-colors"
                                  title="Accept match"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => handleRejectMatch(match.id)}
                                  className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                                  title="Reject match"
                                >
                                  ✗
                                </button>
                                {/* Add New Code button for unmatched items */}
                                {!match.matchedMasterCode && onAddMasterCode && (
                                  <button
                                    onClick={() => handleOpenCreateCode(match)}
                                    className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded transition-colors"
                                    title="Add as new master code"
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {filteredMatches.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <p>No matches found with current filters</p>
                  </div>
                )}
              </div>
              
              {/* Drag & Drop Target for Manual Matching */}
              {draggedMatch && (
                <div className="p-4 border-t border-gray-700 bg-gray-800/50">
                  <p className="text-sm text-gray-400 mb-2">
                    Drag <span className="text-cyan-400 font-mono">{draggedMatch.uploadedCode}</span> to a master code below:
                  </p>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-auto">
                    {masterCodes.slice(0, 50).map(mc => (
                      <div
                        key={mc.code}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-cyan-600 rounded text-sm cursor-pointer transition-colors"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleDropOnMaster(mc.code)}
                      >
                        <span className="font-mono font-bold text-cyan-400">{mc.code}</span>
                        <span className="text-gray-400 ml-2 text-xs">{mc.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Finalize Button */}
              <div className="p-4 border-t border-gray-700 bg-gray-800/50">
                <button
                  onClick={handleFinalize}
                  disabled={acceptedCount === 0}
                  className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg font-medium transition-all disabled:cursor-not-allowed"
                >
                  ✓ Finalize {acceptedCount} Accepted Matches
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  This will add the uploaded codes as aliases to the master codes
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Create New Code Modal */}
      {creatingNewCode && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
          <div className="bg-gray-800 rounded-xl border border-gray-600 w-full max-w-lg mx-4 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div>
                <h3 className="text-lg font-bold text-white">Create New Master Code</h3>
                <p className="text-sm text-gray-400">
                  Add "{creatingNewCode.uploadedCode}" as a new code to your standards
                </p>
              </div>
              <button
                onClick={() => setCreatingNewCode(null)}
                className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            {/* Form */}
            <div className="p-4 space-y-4">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Code <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newCodeForm.code}
                  onChange={(e) => setNewCodeForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono focus:border-cyan-500 focus:outline-none"
                  placeholder="e.g., DV"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newCodeForm.description}
                  onChange={(e) => setNewCodeForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                  placeholder="e.g., Drain Valve"
                />
              </div>
              
              {/* Point Layer */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Point Layer
                </label>
                {editingLayerField === 'pointLayer' ? (
                  <div className="relative">
                    <input
                      type="text"
                      value={layerSearch}
                      onChange={(e) => setLayerSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setEditingLayerField(null);
                          setLayerSearch('');
                          setLayerAiResults([]);
                        }
                      }}
                      placeholder="Search layers..."
                      className="w-full px-3 py-2 bg-gray-700 border border-cyan-500 rounded-lg text-white font-mono focus:outline-none"
                      autoFocus
                    />
                    {/* Dropdown */}
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-20 max-h-64 overflow-auto">
                      {/* AI Results */}
                      {layerAiResults.length > 0 && (
                        <>
                          <div className="px-3 py-1.5 bg-purple-500/20 text-purple-300 text-xs font-medium border-b border-gray-700">
                            ✨ AI Suggested Layers
                          </div>
                          {layerAiResults.map(layer => (
                            <button
                              key={`ai-${layer}`}
                              onClick={() => {
                                setNewCodeForm(prev => ({ ...prev, pointLayer: layer }));
                                setEditingLayerField(null);
                                setLayerSearch('');
                                setLayerAiResults([]);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-purple-500/20 text-purple-300 font-mono text-sm border-b border-gray-700"
                            >
                              {layer}
                            </button>
                          ))}
                          <div className="border-b-2 border-gray-600" />
                        </>
                      )}
                      
                      {/* Regular results */}
                      {filteredLayers.length > 0 && (
                        <>
                          {layerAiResults.length > 0 && (
                            <div className="px-3 py-1.5 bg-gray-700/50 text-gray-400 text-xs font-medium border-b border-gray-700">
                              Text Matches
                            </div>
                          )}
                          {filteredLayers.map(layer => (
                            <button
                              key={layer}
                              onClick={() => {
                                setNewCodeForm(prev => ({ ...prev, pointLayer: layer }));
                                setEditingLayerField(null);
                                setLayerSearch('');
                                setLayerAiResults([]);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-700 text-cyan-400 font-mono text-sm border-b border-gray-700 last:border-0"
                            >
                              {layer}
                            </button>
                          ))}
                        </>
                      )}
                      
                      {/* No results + AI search */}
                      {filteredLayers.length === 0 && layerAiResults.length === 0 && !layerAiSearching && (
                        <div className="px-3 py-3 text-center">
                          <p className="text-gray-500 text-sm mb-2">No direct matches</p>
                          <button
                            onClick={() => handleLayerAiSearch(layerSearch)}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg"
                          >
                            ✨ AI Search
                          </button>
                        </div>
                      )}
                      
                      {/* AI Search button when results exist */}
                      {filteredLayers.length > 0 && layerAiResults.length === 0 && layerSearch.trim() && (
                        <button
                          onClick={() => handleLayerAiSearch(layerSearch)}
                          disabled={layerAiSearching}
                          className="w-full px-3 py-2.5 text-left hover:bg-purple-500/20 text-purple-400 border-t border-gray-600"
                        >
                          {layerAiSearching ? '⏳ Searching...' : `✨ Try AI Search for "${layerSearch}"`}
                        </button>
                      )}
                      
                      {layerAiSearching && (
                        <div className="px-3 py-4 text-center text-purple-400 animate-pulse">
                          ✨ AI searching...
                        </div>
                      )}
                    </div>
                    {/* Click outside to close */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => {
                        setEditingLayerField(null);
                        setLayerSearch('');
                        setLayerAiResults([]);
                      }}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingLayerField('pointLayer');
                      setLayerSearch(newCodeForm.pointLayer || '');
                      setLayerAiResults([]);
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-left font-mono hover:border-cyan-500 transition-colors"
                  >
                    {newCodeForm.pointLayer ? (
                      <span className="text-cyan-400">{newCodeForm.pointLayer}</span>
                    ) : (
                      <span className="text-gray-500">Click to search layers...</span>
                    )}
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-1">A/E/C layer for point symbols</p>
              </div>
              
              {/* Line Layer */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Line Layer
                </label>
                {editingLayerField === 'lineLayer' ? (
                  <div className="relative">
                    <input
                      type="text"
                      value={layerSearch}
                      onChange={(e) => setLayerSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setEditingLayerField(null);
                          setLayerSearch('');
                          setLayerAiResults([]);
                        }
                      }}
                      placeholder="Search layers..."
                      className="w-full px-3 py-2 bg-gray-700 border border-cyan-500 rounded-lg text-white font-mono focus:outline-none"
                      autoFocus
                    />
                    {/* Dropdown */}
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-20 max-h-64 overflow-auto">
                      {/* AI Results */}
                      {layerAiResults.length > 0 && (
                        <>
                          <div className="px-3 py-1.5 bg-purple-500/20 text-purple-300 text-xs font-medium border-b border-gray-700">
                            ✨ AI Suggested Layers
                          </div>
                          {layerAiResults.map(layer => (
                            <button
                              key={`ai-${layer}`}
                              onClick={() => {
                                setNewCodeForm(prev => ({ ...prev, lineLayer: layer }));
                                setEditingLayerField(null);
                                setLayerSearch('');
                                setLayerAiResults([]);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-purple-500/20 text-purple-300 font-mono text-sm border-b border-gray-700"
                            >
                              {layer}
                            </button>
                          ))}
                          <div className="border-b-2 border-gray-600" />
                        </>
                      )}
                      
                      {/* Regular results */}
                      {filteredLayers.length > 0 && (
                        <>
                          {layerAiResults.length > 0 && (
                            <div className="px-3 py-1.5 bg-gray-700/50 text-gray-400 text-xs font-medium border-b border-gray-700">
                              Text Matches
                            </div>
                          )}
                          {filteredLayers.map(layer => (
                            <button
                              key={layer}
                              onClick={() => {
                                setNewCodeForm(prev => ({ ...prev, lineLayer: layer }));
                                setEditingLayerField(null);
                                setLayerSearch('');
                                setLayerAiResults([]);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-700 text-cyan-400 font-mono text-sm border-b border-gray-700 last:border-0"
                            >
                              {layer}
                            </button>
                          ))}
                        </>
                      )}
                      
                      {/* No results + AI search */}
                      {filteredLayers.length === 0 && layerAiResults.length === 0 && !layerAiSearching && (
                        <div className="px-3 py-3 text-center">
                          <p className="text-gray-500 text-sm mb-2">No direct matches</p>
                          <button
                            onClick={() => handleLayerAiSearch(layerSearch)}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg"
                          >
                            ✨ AI Search
                          </button>
                        </div>
                      )}
                      
                      {/* AI Search button when results exist */}
                      {filteredLayers.length > 0 && layerAiResults.length === 0 && layerSearch.trim() && (
                        <button
                          onClick={() => handleLayerAiSearch(layerSearch)}
                          disabled={layerAiSearching}
                          className="w-full px-3 py-2.5 text-left hover:bg-purple-500/20 text-purple-400 border-t border-gray-600"
                        >
                          {layerAiSearching ? '⏳ Searching...' : `✨ Try AI Search for "${layerSearch}"`}
                        </button>
                      )}
                      
                      {layerAiSearching && (
                        <div className="px-3 py-4 text-center text-purple-400 animate-pulse">
                          ✨ AI searching...
                        </div>
                      )}
                    </div>
                    {/* Click outside to close */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => {
                        setEditingLayerField(null);
                        setLayerSearch('');
                        setLayerAiResults([]);
                      }}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingLayerField('lineLayer');
                      setLayerSearch(newCodeForm.lineLayer || '');
                      setLayerAiResults([]);
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-left font-mono hover:border-cyan-500 transition-colors"
                  >
                    {newCodeForm.lineLayer ? (
                      <span className="text-cyan-400">{newCodeForm.lineLayer}</span>
                    ) : (
                      <span className="text-gray-500">Click to search layers... (optional)</span>
                    )}
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-1">A/E/C layer for linework (leave empty if point-only)</p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={() => setCreatingNewCode(null)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewCode}
                disabled={!newCodeForm.code || !newCodeForm.description}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
              >
                ➕ Create & Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Default empty session
export const createEmptySurveyorSession = (): SurveyorCodeSession => ({
  surveyors: [],
  uploads: [],
  matches: [],
  lastUpdated: new Date().toISOString(),
});
